import {
    Box,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import { ArrowRight, MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { CaseDto } from '~/backend/connector-next-api/connector-next-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { SELECTED_CHIP_SX } from '../shared';

const CASE_SX: SxProps<Theme> = {
    height: 36,
    px: 0.5,
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 2,
};

/** Selected as every toggle chip in the panel is; the case chip's size is what sets it apart. */
const SELECTED_CASE_SX: SxProps<Theme> = {
    ...CASE_SX,
    ...SELECTED_CHIP_SX,
    borderWidth: 2,
};

/** What the selected case's menu can do to it. */
export type CaseAction =
    | 'rename'
    | 'workCase'
    | 'archive'
    | 'restore'
    | 'delete';

type CaseChipsProps = {
    /** Cases not archived, in the order the Connector lists them. */
    cases: readonly CaseDto[];
    archivedCases: readonly CaseDto[];
    /** The case work opens on, marked with an arrow. */
    workCaseId: string | undefined;
    /** The case in view; undefined is "All objects". */
    selectedCaseId: string | undefined;
    /** How many datasets "All objects" covers. */
    allCount: number;
    onSelect: (caseId: string | undefined) => void;
    /** Absent when the user may not change cases: the case menu is not offered. */
    onAction?: (action: CaseAction, caseId: string) => void;
    /** How many datasets are ticked; the New case button shows only when some are. */
    newCaseCount: number;
    /** Absent when the user may not change cases: New case is not offered. */
    onNewCase?: () => void;
    busy: boolean;
};

/**
 * The patient's cases as the panel's main choice: each case with its object
 * count, the work case marked, then archived cases behind a toggle, then a
 * quieter "All objects" to fall back to. The selected case carries a menu to
 * rename, archive or restore, delete it, or make it the work case. New case
 * makes a case of the selected datasets.
 */
export default function CaseChips({
    cases,
    archivedCases,
    workCaseId,
    selectedCaseId,
    allCount,
    onSelect,
    onAction,
    newCaseCount,
    onNewCase,
    busy,
}: CaseChipsProps) {
    const intl = useIntl();
    const selectedIsArchived = archivedCases.some(
        (c) => c.id === selectedCaseId
    );
    const [showArchived, setShowArchived] = useState(selectedIsArchived);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const shown = showArchived ? [...cases, ...archivedCases] : cases;
    const selected = shown.find((c) => c.id === selectedCaseId);

    const act = (action: CaseAction) => {
        setMenuAnchor(null);
        if (selected?.id) {
            onAction?.(action, selected.id);
        }
    };

    return (
        <Box
            role="group"
            aria-label={intl.formatMessage({ id: 'patientList.cases.chips' })}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 2,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Typography
                component="span"
                sx={{ fontSize: 15, fontWeight: 600, mr: 0.5 }}
            >
                {intl.formatMessage({ id: 'patientList.cases.chips' })}
            </Typography>
            {shown.map((c) => {
                const isSelected = c.id === selectedCaseId;
                const isWork = c.id === workCaseId;
                return (
                    <Chip
                        key={c.id}
                        icon={isWork ? <ArrowRight size={16} /> : undefined}
                        label={
                            <SensitiveInfoTypography>
                                {intl.formatMessage(
                                    {
                                        id: c.isArchived
                                            ? 'patientList.cases.archivedChip'
                                            : 'patientList.cases.chip',
                                    },
                                    {
                                        name: c.name ?? '',
                                        count: c.memberIds?.length ?? 0,
                                    }
                                )}
                            </SensitiveInfoTypography>
                        }
                        title={
                            isWork
                                ? intl.formatMessage({
                                      id: 'patientList.cases.workCase',
                                  })
                                : undefined
                        }
                        variant="outlined"
                        sx={isSelected ? SELECTED_CASE_SX : CASE_SX}
                        aria-pressed={isSelected}
                        onClick={() => onSelect(c.id)}
                    />
                );
            })}
            {cases.length === 0 && archivedCases.length === 0 && (
                <Typography
                    component="span"
                    sx={{ fontSize: 15, color: 'text.secondary' }}
                >
                    {intl.formatMessage({ id: 'patientList.cases.none' })}
                </Typography>
            )}
            {selected !== undefined && onAction && (
                <IconButton
                    size="small"
                    disabled={busy}
                    aria-label={intl.formatMessage({
                        id: 'patientList.cases.menu',
                    })}
                    aria-haspopup="menu"
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                >
                    <MoreHorizontal size={16} />
                </IconButton>
            )}
            {archivedCases.length > 0 && (
                <Chip
                    size="small"
                    variant="outlined"
                    label={intl.formatMessage(
                        {
                            id: showArchived
                                ? 'patientList.cases.hideArchived'
                                : 'patientList.cases.showArchived',
                        },
                        { count: archivedCases.length }
                    )}
                    onClick={() => {
                        if (showArchived && selectedIsArchived) {
                            onSelect(undefined);
                        }
                        setShowArchived(!showArchived);
                    }}
                />
            )}
            <Chip
                size="small"
                label={intl.formatMessage(
                    { id: 'patientList.cases.all' },
                    { count: allCount }
                )}
                variant="outlined"
                sx={selectedCaseId === undefined ? SELECTED_CHIP_SX : undefined}
                aria-pressed={selectedCaseId === undefined}
                onClick={() => onSelect(undefined)}
            />
            {newCaseCount > 0 && onNewCase && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    disabled={busy}
                    onClick={onNewCase}
                    sx={{ ml: 'auto' }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.cases.newOf' },
                        { count: newCaseCount }
                    )}
                </Button>
            )}
            <Menu
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                <MenuItem onClick={() => act('rename')}>
                    {intl.formatMessage({ id: 'patientList.cases.rename' })}
                </MenuItem>
                {selected &&
                    !selected.isArchived &&
                    selected.id !== workCaseId && (
                        <MenuItem onClick={() => act('workCase')}>
                            {intl.formatMessage({
                                id: 'patientList.cases.makeWorkCase',
                            })}
                        </MenuItem>
                    )}
                <MenuItem
                    onClick={() =>
                        act(selected?.isArchived ? 'restore' : 'archive')
                    }
                >
                    {intl.formatMessage({
                        id: selected?.isArchived
                            ? 'patientList.cases.restore'
                            : 'patientList.cases.archive',
                    })}
                </MenuItem>
                <MenuItem
                    onClick={() => act('delete')}
                    sx={{ color: 'error.main' }}
                >
                    {intl.formatMessage({ id: 'patientList.cases.delete' })}
                </MenuItem>
            </Menu>
        </Box>
    );
}
